import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
// import { Separator } from '@/components/ui/Separator';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

interface AutomationCondition {
  field: string;
  operator: string;
  value: string;
  logicalOperator?: 'AND' | 'OR';
}

interface AutomationSegment {
  id?: string;
  name: string;
  conditions: AutomationCondition[];
  description?: string;
  isActive?: boolean;
  stats?: {
    totalClients: number;
    totalTitulos: number;
    totalValue: number;
  };
}

interface AutomationSchedule {
  type: 'immediate' | 'scheduled' | 'recurring';
  scheduledAt?: Date;
  recurringPattern?: {
    frequency: 'daily' | 'weekly' | 'monthly';
    interval: number;
    daysOfWeek?: number[];
    dayOfMonth?: number;
  };
}

interface EmailAutomation {
  id: string;
  name: string;
  description?: string;
  triggerType: string;
  triggerValue: number;
  templateId: string;
  template: {
    id: string;
    name: string;
    subject: string;
  };
  isActive: boolean;
  conditions: AutomationCondition[];
  segments: AutomationSegment[];
  schedule: AutomationSchedule;
  retryConfig: {
    maxRetries: number;
    retryDelay: number;
  };
  throttling: {
    maxEmailsPerHour: number;
    maxEmailsPerDay: number;
  };
  createdAt: string;
  executions: any[];
  _count: {
    executions: number;
  };
}

// interface AutomationStats {
//   totalExecutions: number;
//   successfulExecutions: number;
//   failedExecutions: number;
//   lastExecution?: Date;
//   emailsSent: number;
//   successRate: number;
//   averageExecutionTime: number;
//   retryCount: number;
//   segmentStats: {
//     segmentName: string;
//     emailsSent: number;
//     openRate: number;
//     clickRate: number;
//   }[];
// }

interface AutomationAnalytics {
  automationId: string;
  period: string;
  totalExecutions: number;
  totalEmailsSent: number;
  totalEmailsFailed: number;
  averageExecutionTime: number;
  dailyMetrics: any[];
  trends: {
    emailsSentTrend: number;
    successRateTrend: number;
    executionTimeTrend: number;
  };
  segmentPerformance: any[];
}

const EmailAutomationPage: React.FC = () => {
  const navigate = useNavigate();
  const [automations, setAutomations] = useState<EmailAutomation[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [triggerTypes, setTriggerTypes] = useState<any[]>([]);
  const [conditionFields, setConditionFields] = useState<any[]>([]);
  const [operators, setOperators] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSegmentModal, setShowSegmentModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedAutomation, setSelectedAutomation] = useState<EmailAutomation | null>(null);
  const [analytics, setAnalytics] = useState<AutomationAnalytics | null>(null);
  const [activeTab, setActiveTab] = useState('automations');
  // Analytics tab state
  const [selectedAnalyticsAutomationId, setSelectedAnalyticsAutomationId] = useState<string>('');
  const [analyticsLoadingTab, setAnalyticsLoadingTab] = useState<boolean>(false);
  const [analyticsStatsTab, setAnalyticsStatsTab] = useState<any | null>(null);
  const [analyticsExecutionsTab, setAnalyticsExecutionsTab] = useState<any[]>([]);
  const [analyticsDailyTab, setAnalyticsDailyTab] = useState<Array<{ date: string; executions: number; emailsSent: number; emailsFailed: number; avgExecutionTime: number }>>([]);

  // Estados para gerenciamento de segmentos
  const [, setSelectedSegment] = useState<AutomationSegment | null>(null);
  const [segmentSearchTerm, setSegmentSearchTerm] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('all');

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    triggerType: '',
    triggerValue: 0,
    templateId: '',
    isActive: true,
    conditions: [] as AutomationCondition[],
    segments: [] as AutomationSegment[],
    schedule: {
      type: 'immediate' as const,
    } as AutomationSchedule,
    retryConfig: {
      maxRetries: 3,
      retryDelay: 5,
    },
    throttling: {
      maxEmailsPerHour: 1000,
      maxEmailsPerDay: 10000,
    },
  });

  // Segment form data
  const [segmentFormData, setSegmentFormData] = useState({
    name: '',
    description: '',
    conditions: [] as AutomationCondition[],
  });

  // Schedule form data
  const [scheduleFormData, setScheduleFormData] = useState<AutomationSchedule>({
    type: 'immediate',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [automationsRes, templatesRes, triggerTypesRes, conditionFieldsRes, operatorsRes] = await Promise.all([
        api.get('/email-automation'),
        api.get('/email-templates?isActive=true'),
        api.get('/email-automation/trigger-types'),
        api.get('/email-automation/condition-fields'),
        api.get('/email-automation/operators'),
      ]);

      setAutomations(Array.isArray(automationsRes.data?.data) ? automationsRes.data.data : []);
      setTemplates(Array.isArray(templatesRes.data?.data) ? templatesRes.data.data : []);
      setTriggerTypes(triggerTypesRes.data.data);
      setConditionFields(conditionFieldsRes.data.data);
      setOperators(operatorsRes.data.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!formData.templateId) {
        toast.error('Selecione um template de email');
        return;
      }
      if (selectedAutomation) {
        await api.put(`/email-automation/${selectedAutomation.id}`, formData);
        toast.success('Automação atualizada com sucesso');
      } else {
        await api.post('/email-automation', formData);
        toast.success('Automação criada com sucesso');
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao salvar automação');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta automação?')) return;
    
    try {
      await api.delete(`/email-automation/${id}`);
      toast.success('Automação excluída com sucesso');
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao excluir automação');
    }
  };

  const handleToggleAutomation = async (id: string) => {
    try {
      await api.patch(`/email-automation/${id}/toggle`);
      toast.success('Status da automação alterado');
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const handleExecuteAutomation = async (id: string) => {
    try {
      const response = await api.post(`/email-automation/${id}/execute`);
      const result = response.data.data;
      toast.success(`Automação executada com sucesso. ${result.emailsSent} emails enviados.`);
      if (result.emailsFailed > 0) {
        toast.error(`${result.emailsFailed} emails falharam`);
      }
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao executar automação');
    }
  };

  const handleScheduleAutomation = async (id: string) => {
    try {
      await api.post(`/email-automation/${id}/schedule`, { schedule: scheduleFormData });
      toast.success('Agendamento configurado com sucesso');
      setShowScheduleModal(false);
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao configurar agendamento');
    }
  };

  const handleAddSegment = async (automationId: string) => {
    try {
      await api.post(`/email-automation/${automationId}/segments`, { segment: segmentFormData });
      toast.success('Segmento criado com sucesso');
      setShowSegmentModal(false);
      resetSegmentForm();
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao criar segmento');
    }
  };

  const handleDeleteSegmentFromAutomation = async (automationId: string, segmentIndex: number) => {
    try {
      await api.delete(`/email-automation/${automationId}/segments/${segmentIndex}`);
      toast.success('Segmento excluído com sucesso');
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao excluir segmento');
    }
  };

  const loadAnalytics = async (automationId: string) => {
    try {
      const [statsRes, execRes] = await Promise.all([
        api.get(`/email-automation/${automationId}/stats`),
        api.get(`/email-automation/${automationId}/executions`),
      ]);
      const stats = statsRes.data?.data || {};
      const executions: any[] = Array.isArray(execRes.data?.data) ? execRes.data.data : [];

      // Calcular métricas diárias e tempo médio
      const dailyMap = new Map<string, { executions: number; emailsSent: number; emailsFailed: number; execTimes: number[] }>();
      executions.forEach((e) => {
        const date = new Date(e.createdAt).toISOString().slice(0, 10);
        const entry = dailyMap.get(date) || { executions: 0, emailsSent: 0, emailsFailed: 0, execTimes: [] };
        entry.executions += 1;
        entry.emailsSent += e.emailsSent || 0;
        entry.emailsFailed += e.emailsFailed || 0;
        if (typeof e.executionTime === 'number') entry.execTimes.push(e.executionTime);
        dailyMap.set(date, entry);
      });
      const dailyMetrics = Array.from(dailyMap.entries()).map(([date, d]) => ({
        date,
        executions: d.executions,
        emailsSent: d.emailsSent,
        emailsFailed: d.emailsFailed,
        avgExecutionTime: d.execTimes.length ? Math.round(d.execTimes.reduce((a, b) => a + b, 0) / d.execTimes.length) : 0,
      })).sort((a, b) => a.date.localeCompare(b.date));

      // Tempo médio geral
      const allTimes = executions.map(e => e.executionTime).filter((n: any) => typeof n === 'number');
      const averageExecutionTime = allTimes.length ? Math.round(allTimes.reduce((a: number, b: number) => a + b, 0) / allTimes.length) : 0;

      setAnalytics({ ...stats, dailyMetrics, trends: { emailsSentTrend: 0, successRateTrend: 0, executionTimeTrend: 0 }, averageExecutionTime });
      setShowAnalyticsModal(true);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar analytics');
    }
  };

  const loadAutomationAnalyticsTab = async (automationId: string) => {
    if (!automationId) {
      setAnalyticsStatsTab(null);
      setAnalyticsExecutionsTab([]);
      setAnalyticsDailyTab([]);
      return;
    }
    try {
      setAnalyticsLoadingTab(true);
      const [statsRes, execRes] = await Promise.all([
        api.get(`/email-automation/${automationId}/stats`),
        api.get(`/email-automation/${automationId}/executions`),
      ]);
      const stats = statsRes.data?.data || {};
      const executions: any[] = Array.isArray(execRes.data?.data) ? execRes.data.data : [];

      const dailyMap = new Map<string, { executions: number; emailsSent: number; emailsFailed: number; execTimes: number[] }>();
      executions.forEach((e) => {
        const date = new Date(e.createdAt).toISOString().slice(0, 10);
        const entry = dailyMap.get(date) || { executions: 0, emailsSent: 0, emailsFailed: 0, execTimes: [] };
        entry.executions += 1;
        entry.emailsSent += e.emailsSent || 0;
        entry.emailsFailed += e.emailsFailed || 0;
        if (typeof e.executionTime === 'number') entry.execTimes.push(e.executionTime);
        dailyMap.set(date, entry);
      });
      const daily = Array.from(dailyMap.entries()).map(([date, d]) => ({
        date,
        executions: d.executions,
        emailsSent: d.emailsSent,
        emailsFailed: d.emailsFailed,
        avgExecutionTime: d.execTimes.length ? Math.round(d.execTimes.reduce((a, b) => a + b, 0) / d.execTimes.length) : 0,
      })).sort((a, b) => a.date.localeCompare(b.date));

      // compute avg execution time
      const allTimes = executions.map(e => e.executionTime).filter((n: any) => typeof n === 'number');
      const averageExecutionTime = allTimes.length ? Math.round(allTimes.reduce((a: number, b: number) => a + b, 0) / allTimes.length) : 0;

      setAnalyticsStatsTab({ ...stats, averageExecutionTime });
      setAnalyticsExecutionsTab(executions);
      setAnalyticsDailyTab(daily);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar analytics');
    } finally {
      setAnalyticsLoadingTab(false);
    }
  };

  // Funções para gerenciamento de segmentos
  const getFilteredSegments = (): AutomationSegment[] => {
    let allSegments: AutomationSegment[] = [];
    
    // Coletar todos os segmentos de todas as automações
    automations.forEach(automation => {
      if (Array.isArray(automation.segments)) {
        allSegments = allSegments.concat(automation.segments.map(segment => ({
          ...segment,
          id: `${automation.id}-${segment.name}`, // ID único para cada segmento
          stats: {
            totalClients: Math.floor(Math.random() * 100) + 10, // Mock data
            totalTitulos: Math.floor(Math.random() * 50) + 5,
            totalValue: Math.floor(Math.random() * 100000) + 10000,
          }
        })));
      }
    });

    // Aplicar filtro de busca
    if (segmentSearchTerm) {
      allSegments = allSegments.filter(segment =>
        segment.name.toLowerCase().includes(segmentSearchTerm.toLowerCase()) ||
        segment.description?.toLowerCase().includes(segmentSearchTerm.toLowerCase())
      );
    }

    // Aplicar filtro de status
    switch (segmentFilter) {
      case 'active':
        allSegments = allSegments.filter(segment => segment.isActive !== false);
        break;
      case 'inactive':
        allSegments = allSegments.filter(segment => segment.isActive === false);
        break;
      case 'high-value':
        allSegments = allSegments.filter(segment => (segment.stats?.totalValue || 0) > 50000);
        break;
      case 'medium-value':
        allSegments = allSegments.filter(segment => {
          const value = segment.stats?.totalValue || 0;
          return value >= 10000 && value <= 50000;
        });
        break;
      case 'low-value':
        allSegments = allSegments.filter(segment => (segment.stats?.totalValue || 0) < 10000);
        break;
    }

    return allSegments;
  };

  const handleToggleSegment = async (_segmentId: string) => {
    try {
      // Mock function - em produção seria uma chamada API
      toast.success('Status do segmento alterado');
      loadData();
    } catch (error: any) {
      toast.error('Erro ao alterar status do segmento');
    }
  };

  const handleViewSegmentDetails = async (_segmentId: string) => {
    try {
      // Mock function - em produção seria uma chamada API
      toast.success('Detalhes do segmento carregados');
    } catch (error: any) {
      toast.error('Erro ao carregar detalhes do segmento');
    }
  };

  const handleDeleteSegment = async (_segmentId: string) => {
    if (!confirm('Tem certeza que deseja excluir este segmento?')) return;
    
    try {
      // Mock function - em produção seria uma chamada API
      toast.success('Segmento excluído com sucesso');
      loadData();
    } catch (error: any) {
      toast.error('Erro ao excluir segmento');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      triggerType: '',
      triggerValue: 0,
      templateId: '',
      isActive: true,
      conditions: [],
      segments: [],
      schedule: { type: 'immediate' },
      retryConfig: { maxRetries: 3, retryDelay: 5 },
      throttling: { maxEmailsPerHour: 1000, maxEmailsPerDay: 10000 },
    });
    setSelectedAutomation(null);
  };

  const resetSegmentForm = () => {
    setSegmentFormData({
      name: '',
      description: '',
      conditions: [],
    });
  };

  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { field: '', operator: '', value: '' }],
    }));
  };

  const removeCondition = (index: number) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  const updateCondition = (index: number, field: keyof AutomationCondition, value: string) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((condition, i) =>
        i === index ? { ...condition, [field]: value } : condition
      ),
    }));
  };

  const addSegmentCondition = () => {
    setSegmentFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { field: '', operator: '', value: '' }],
    }));
  };

  const removeSegmentCondition = (index: number) => {
    setSegmentFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  const updateSegmentCondition = (index: number, field: keyof AutomationCondition, value: string) => {
    setSegmentFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((condition, i) =>
        i === index ? { ...condition, [field]: value } : condition
      ),
    }));
  };

  const openEditModal = (automation: EmailAutomation) => {
    setSelectedAutomation(automation);
    setFormData({
      name: automation.name,
      description: automation.description || '',
      triggerType: automation.triggerType,
      triggerValue: automation.triggerValue,
      templateId: automation.templateId,
      isActive: automation.isActive,
      conditions: automation.conditions,
      segments: automation.segments,
      schedule: automation.schedule,
      retryConfig: automation.retryConfig,
      throttling: automation.throttling,
    });
    setShowModal(true);
  };

  const getTriggerTypeLabel = (value: string) => {
    const trigger = triggerTypes.find(t => t.value === value);
    return trigger ? trigger.label : value;
  };

  const getStatusBadge = (isActive: boolean) => (
    <Badge variant={isActive ? 'default' : 'secondary'}>
      {isActive ? 'Ativa' : 'Inativa'}
    </Badge>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Automação de Emails</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/configuracoes')}>← Voltar para Configurações &gt; E-mails</Button>
          <Button onClick={() => setShowModal(true)}>Nova Automação</Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="automations">Automações</TabsTrigger>
          <TabsTrigger value="segments">Segmentos</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="automations" className="space-y-4">
          
          <div className="grid gap-4">
            {Array.isArray(automations) && automations.map((automation) => {
              return (
                <Card key={automation.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {automation.name}
                          {getStatusBadge(automation.isActive)}
                        </CardTitle>
                        <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">
                          {automation.description}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditModal(automation)}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleExecuteAutomation(automation.id)}
                        >
                          Executar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleAutomation(automation.id)}
                        >
                          {automation.isActive ? 'Desativar' : 'Ativar'}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(automation.id)}
                        >
                          Excluir
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Trigger:</span>
                        <p>{getTriggerTypeLabel(automation.triggerType)}</p>
                      </div>
                      <div>
                        <span className="font-medium">Template:</span>
                        <p>{automation.template?.name || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="font-medium">Execuções:</span>
                        <p>{automation._count?.executions || 0}</p>
                      </div>
                      <div>
                        <span className="font-medium">Segmentos:</span>
                        <p>{Array.isArray(automation.segments) ? automation.segments.length : 0}</p>
                      </div>
                    </div>
                    
                    {automation.segments && Array.isArray(automation.segments) && automation.segments.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-medium mb-2">Segmentos:</h4>
                        <div className="flex flex-wrap gap-2">
                          {automation.segments.map((segment, index) => (
                            <Badge key={index} variant="outline">
                              {segment.name}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="ml-1 h-4 w-4 p-0"
                                onClick={() => handleDeleteSegmentFromAutomation(automation.id, index)}
                              >
                                ×
                              </Button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2 mt-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedAutomation(automation);
                          setShowSegmentModal(true);
                        }}
                      >
                        Adicionar Segmento
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedAutomation(automation);
                          setShowScheduleModal(true);
                        }}
                      >
                        Configurar Agendamento
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => loadAnalytics(automation.id)}
                      >
                        Ver Analytics
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="segments" className="space-y-6">
          {/* Header da aba Segmentos */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Gerenciamento de Segmentos</h2>
              <p className="text-gray-600 dark:text-slate-300 mt-1">
                Crie e gerencie segmentos de clientes para automações mais precisas e personalizadas
              </p>
            </div>
            <Button 
              onClick={() => {
                setSelectedSegment(null);
                setShowSegmentModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              + Novo Segmento
            </Button>
          </div>

          {/* Estatísticas dos Segmentos */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600 dark:text-slate-300">Total de Segmentos</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                      {Array.isArray(automations) ? automations.reduce((total, automation) => 
                        total + (Array.isArray(automation.segments) ? automation.segments.length : 0), 0) : 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600 dark:text-slate-300">Segmentos Ativos</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                      {Array.isArray(automations) ? automations.reduce((total, automation) => 
                        total + (Array.isArray(automation.segments) ? automation.segments.filter(s => s.isActive !== false).length : 0), 0) : 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600 dark:text-slate-300">Performance Média</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">87%</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-600 dark:text-slate-300">Última Atualização</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">2h</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Lista de Segmentos */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Segmentos Disponíveis</CardTitle>
                <div className="flex gap-2">
                  <Input
                    placeholder="Buscar segmentos..."
                    className="w-64"
                    value={segmentSearchTerm}
                    onChange={(e) => setSegmentSearchTerm(e.target.value)}
                  />
                  <Select
                    value={segmentFilter}
                    onValueChange={(value) => setSegmentFilter(value)}
                  >
                    <option value="all">Todos</option>
                    <option value="active">Ativos</option>
                    <option value="inactive">Inativos</option>
                    <option value="high-value">Alto Valor</option>
                    <option value="medium-value">Médio Valor</option>
                    <option value="low-value">Baixo Valor</option>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {getFilteredSegments().map((segment, index) => (
                  <div key={index} className="border rounded-lg p-4 hover:bg-gray-50 dark:bg-slate-800 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 text-gray-900 dark:text-slate-100">{segment.name}</h3>
                          <Badge variant={segment.isActive !== false ? "default" : "secondary"}>
                            {segment.isActive !== false ? "Ativo" : "Inativo"}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {segment.conditions?.length || 0} condições
                          </Badge>
                        </div>
                        <p className="text-gray-600 dark:text-slate-300 mb-3">{segment.description}</p>
                        
                        {/* Condições do Segmento */}
                        {Array.isArray(segment.conditions) && segment.conditions.length > 0 && (
                          <div className="mb-3">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">Condições:</h4>
                            <div className="flex flex-wrap gap-2">
                              {segment.conditions.map((condition, condIndex) => (
                                <Badge key={condIndex} variant="outline" className="text-xs">
                                  {condition.field} {condition.operator} {condition.value}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Estatísticas do Segmento */}
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500 dark:text-slate-400">Clientes:</span>
                            <span className="ml-1 font-medium">{segment.stats?.totalClients || 0}</span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-slate-400">Títulos:</span>
                            <span className="ml-1 font-medium">{segment.stats?.totalTitulos || 0}</span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-slate-400">Valor Total:</span>
                            <span className="ml-1 font-medium">
                              R$ {(segment.stats?.totalValue || 0).toLocaleString('pt-BR')}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedSegment(segment);
                            setShowSegmentModal(true);
                          }}
                        >
                          Editar
                        </Button>
                                                 <Button
                           size="sm"
                           variant="outline"
                           onClick={() => segment.id && handleToggleSegment(segment.id)}
                         >
                           {segment.isActive !== false ? 'Desativar' : 'Ativar'}
                         </Button>
                         <Button
                           size="sm"
                           variant="outline"
                           onClick={() => segment.id && handleViewSegmentDetails(segment.id)}
                         >
                           Detalhes
                         </Button>
                         <Button
                           size="sm"
                           variant="destructive"
                           onClick={() => segment.id && handleDeleteSegment(segment.id)}
                         >
                           Excluir
                         </Button>
                      </div>
                    </div>
                  </div>
                ))}

                {getFilteredSegments().length === 0 && (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-slate-100">Nenhum segmento encontrado</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
                      {segmentSearchTerm || segmentFilter !== 'all' 
                        ? 'Tente ajustar os filtros de busca.' 
                        : 'Comece criando seu primeiro segmento.'}
                    </p>
                    {!segmentSearchTerm && segmentFilter === 'all' && (
                      <div className="mt-6">
                        <Button
                          onClick={() => {
                            setSelectedSegment(null);
                            setShowSegmentModal(true);
                          }}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          + Criar Primeiro Segmento
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Analytics de Automação</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Selecionar Automação</label>
                  <Select
                    value={selectedAnalyticsAutomationId}
                    onValueChange={(value) => { setSelectedAnalyticsAutomationId(value); loadAutomationAnalyticsTab(value); }}
                  >
                    <option value="">Escolha uma automação</option>
                    {automations.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </Select>
                </div>
              </div>

              {analyticsLoadingTab && (
                <div className="text-center py-8">Carregando analytics...</div>
              )}

              {!analyticsLoadingTab && analyticsStatsTab && (
                <div className="space-y-6 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card><CardContent className="p-4"><div className="text-2xl font-bold">{analyticsStatsTab.totalExecutions}</div><div className="text-sm text-gray-600 dark:text-slate-300">Execuções</div></CardContent></Card>
                    <Card><CardContent className="p-4"><div className="text-2xl font-bold">{analyticsStatsTab.totalEmailsSent}</div><div className="text-sm text-gray-600 dark:text-slate-300">Emails Enviados</div></CardContent></Card>
                    <Card><CardContent className="p-4"><div className="text-2xl font-bold">{analyticsStatsTab.totalEmailsFailed}</div><div className="text-sm text-gray-600 dark:text-slate-300">Emails Falhados</div></CardContent></Card>
                    <Card><CardContent className="p-4"><div className="text-2xl font-bold">{Math.round(analyticsStatsTab.averageExecutionTime || 0)}ms</div><div className="text-sm text-gray-600 dark:text-slate-300">Tempo Médio</div></CardContent></Card>
                  </div>

                  <Card>
                    <CardHeader><CardTitle>Métricas Diárias</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={analyticsDailyTab}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="emailsSent" fill="#00C49F" name="Enviados" />
                          <Bar dataKey="emailsFailed" fill="#FF8042" name="Falhados" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader><CardTitle>Últimas Execuções</CardTitle></CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left">
                              <th className="py-2">Data</th>
                              <th className="py-2">Enviados</th>
                              <th className="py-2">Falhados</th>
                              <th className="py-2">Tempo (ms)</th>
                              <th className="py-2">Status</th>
                              <th className="py-2">Executado por</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analyticsExecutionsTab.slice(0, 10).map((e, i) => (
                              <tr key={i} className="border-t">
                                <td className="py-2">{new Date(e.createdAt).toLocaleString('pt-BR')}</td>
                                <td className="py-2">{e.emailsSent}</td>
                                <td className="py-2">{e.emailsFailed}</td>
                                <td className="py-2">{e.executionTime}</td>
                                <td className="py-2">{e.status}</td>
                                <td className="py-2">{e.user?.name || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Automação */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={selectedAutomation ? 'Editar Automação' : 'Nova Automação'}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6 max-h-[80vh] overflow-y-auto">
          {/* Seção: Informações Básicas */}
          <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-4 text-gray-800">Informações Básicas</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 text-gray-700 dark:text-slate-200">Nome da Automação</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  className="w-full"
                  placeholder="Ex: Lembrete de Vencimento"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 text-gray-700 dark:text-slate-200">Template de Email</label>
                <Select
                  value={formData.templateId}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, templateId: value }))}
                  className="w-full"
                >
                  <option value="">Selecione um template</option>
                  {Array.isArray(templates) && templates.length > 0 ? (
                    templates
                      .filter((t) => t?.isActive !== false)
                      .map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))
                  ) : (
                    <option value="" disabled>Nenhum template encontrado</option>
                  )}
                </Select>
                {Array.isArray(templates) && templates.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">Crie templates em Configurações &gt; E-mails para habilitar este seletor.</p>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 text-gray-700 dark:text-slate-200">Descrição</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full"
              placeholder="Descreva o propósito desta automação..."
              rows={3}
            />
          </div>

          {/* Seção: Configuração do Trigger */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-4 text-blue-800">Configuração do Trigger</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 text-gray-700 dark:text-slate-200">Tipo de Trigger</label>
                <Select
                  value={formData.triggerType}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, triggerType: value }))}
                  className="w-full"
                >
                  <option value="">Selecione o tipo</option>
                  {Array.isArray(triggerTypes) && triggerTypes.map((trigger) => (
                    <option key={trigger.value} value={trigger.value}>
                      {trigger.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 text-gray-700 dark:text-slate-200">Valor do Trigger</label>
                <Input
                  type="number"
                  value={formData.triggerValue}
                  onChange={(e) => setFormData(prev => ({ ...prev, triggerValue: parseInt(e.target.value) }))}
                  required
                  className="w-full"
                  placeholder="Ex: 3 (dias)"
                />
              </div>
            </div>
          </div>

          {/* Seção: Condições */}
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-4 text-yellow-800">Condições de Filtro</h3>
            <p className="text-sm text-gray-600 dark:text-slate-300 mb-4">Configure as condições que determinam quando a automação será executada</p>
            <div className="space-y-3">
              {Array.isArray(formData.conditions) && formData.conditions.map((condition, index) => (
                <div key={index} className="flex gap-3 items-end p-3 bg-white rounded border">
                  <div className="flex-1">
                    <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-slate-300">Campo</label>
                    <Select
                      value={condition.field}
                      onValueChange={(value) => updateCondition(index, 'field', value)}
                      className="w-full"
                    >
                      <option value="">Selecione o campo</option>
                      {Array.isArray(conditionFields) && conditionFields.map((field) => (
                        <option key={field.value} value={field.value}>
                          {field.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-slate-300">Operador</label>
                    <Select
                      value={condition.operator}
                      onValueChange={(value) => updateCondition(index, 'operator', value)}
                      className="w-full"
                    >
                      <option value="">Selecione o operador</option>
                      {Array.isArray(operators) && operators.map((operator) => (
                        <option key={operator.value} value={operator.value}>
                          {operator.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-slate-300">Valor</label>
                    <Input
                      value={condition.value}
                      onChange={(e) => updateCondition(index, 'value', e.target.value)}
                      placeholder="Digite o valor"
                      className="w-full"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => removeCondition(index)}
                    className="mb-1"
                  >
                    ×
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addCondition} className="w-full">
                + Adicionar Condição
              </Button>
            </div>
          </div>

          {/* Seção: Configurações Avançadas */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-4 text-green-800">Configurações Avançadas</h3>
            
            {/* Configurações de Retry */}
            <div className="mb-6">
              <h4 className="text-md font-medium mb-3 text-gray-700 dark:text-slate-200">Configurações de Retry</h4>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 text-gray-700 dark:text-slate-200">Máximo de Tentativas</label>
                  <Input
                    type="number"
                    value={formData.retryConfig.maxRetries}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      retryConfig: { ...prev.retryConfig, maxRetries: parseInt(e.target.value) }
                    }))}
                    className="w-full"
                    placeholder="Ex: 3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 text-gray-700 dark:text-slate-200">Intervalo entre Tentativas (min)</label>
                  <Input
                    type="number"
                    value={formData.retryConfig.retryDelay}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      retryConfig: { ...prev.retryConfig, retryDelay: parseInt(e.target.value) }
                    }))}
                    className="w-full"
                    placeholder="Ex: 5"
                  />
                </div>
              </div>
            </div>

            {/* Configurações de Throttling */}
            <div>
              <h4 className="text-md font-medium mb-3 text-gray-700 dark:text-slate-200">Limites de Envio</h4>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 text-gray-700 dark:text-slate-200">Máximo de Emails/Hora</label>
                  <Input
                    type="number"
                    value={formData.throttling.maxEmailsPerHour}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      throttling: { ...prev.throttling, maxEmailsPerHour: parseInt(e.target.value) }
                    }))}
                    className="w-full"
                    placeholder="Ex: 1000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2 text-gray-700 dark:text-slate-200">Máximo de Emails/Dia</label>
                  <Input
                    type="number"
                    value={formData.throttling.maxEmailsPerDay}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      throttling: { ...prev.throttling, maxEmailsPerDay: parseInt(e.target.value) }
                    }))}
                    className="w-full"
                    placeholder="Ex: 10000"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-slate-700">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowModal(false)}
              className="px-6"
            >
              Cancelar
            </Button>
            <Button 
              type="submit"
              className="px-6 bg-blue-600 hover:bg-blue-700"
            >
              {selectedAutomation ? 'Atualizar Automação' : 'Criar Automação'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal de Segmento */}
      <Modal
        isOpen={showSegmentModal}
        onClose={() => {
          setShowSegmentModal(false);
          resetSegmentForm();
        }}
        title="Adicionar Segmento"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Nome do Segmento</label>
            <Input
              value={segmentFormData.name}
              onChange={(e) => setSegmentFormData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Descrição</label>
            <Textarea
              value={segmentFormData.description}
              onChange={(e) => setSegmentFormData(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Condições</label>
            <div className="space-y-2">
              {Array.isArray(segmentFormData.conditions) && segmentFormData.conditions.map((condition, index) => (
                <div key={index} className="flex gap-2">
                  <Select
                    value={condition.field}
                    onValueChange={(value) => updateSegmentCondition(index, 'field', value)}
                  >
                    <option value="">Campo</option>
                    {Array.isArray(conditionFields) && conditionFields.map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </Select>
                  <Select
                    value={condition.operator}
                    onValueChange={(value) => updateSegmentCondition(index, 'operator', value)}
                  >
                    <option value="">Operador</option>
                    {Array.isArray(operators) && operators.map((operator) => (
                      <option key={operator.value} value={operator.value}>
                        {operator.label}
                      </option>
                    ))}
                  </Select>
                  <Input
                    value={condition.value}
                    onChange={(e) => updateSegmentCondition(index, 'value', e.target.value)}
                    placeholder="Valor"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => removeSegmentCondition(index)}
                  >
                    Remover
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addSegmentCondition}>
                Adicionar Condição
              </Button>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => setShowSegmentModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => selectedAutomation && handleAddSegment(selectedAutomation.id)}
              disabled={!segmentFormData.name || !Array.isArray(segmentFormData.conditions) || segmentFormData.conditions.length === 0}
            >
              Adicionar Segmento
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Agendamento */}
      <Modal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        title="Configurar Agendamento"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Tipo de Agendamento</label>
            <Select
              value={scheduleFormData.type}
              onValueChange={(value: any) => setScheduleFormData(prev => ({ ...prev, type: value }))}
            >
              <option value="immediate">Imediato</option>
              <option value="scheduled">Agendado</option>
              <option value="recurring">Recorrente</option>
            </Select>
          </div>

          {scheduleFormData.type === 'scheduled' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Data e Hora</label>
              <Input
                type="datetime-local"
                onChange={(e) => setScheduleFormData(prev => ({
                  ...prev,
                  scheduledAt: new Date(e.target.value)
                }))}
              />
            </div>
          )}

          {scheduleFormData.type === 'recurring' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Frequência</label>
                <Select
                  value={scheduleFormData.recurringPattern?.frequency || 'daily'}
                  onValueChange={(value: any) => setScheduleFormData(prev => ({
                    ...prev,
                    recurringPattern: { 
                      ...prev.recurringPattern, 
                      frequency: value as 'daily' | 'weekly' | 'monthly',
                      interval: prev.recurringPattern?.interval || 1
                    }
                  }))}
                >
                  <option value="daily">Diário</option>
                  <option value="weekly">Semanal</option>
                  <option value="monthly">Mensal</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Intervalo</label>
                <Input
                  type="number"
                  value={scheduleFormData.recurringPattern?.interval || 1}
                  onChange={(e) => setScheduleFormData(prev => ({
                    ...prev,
                    recurringPattern: { 
                      ...prev.recurringPattern, 
                      interval: parseInt(e.target.value),
                      frequency: prev.recurringPattern?.frequency || 'daily'
                    }
                  }))}
                />
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => setShowScheduleModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => selectedAutomation && handleScheduleAutomation(selectedAutomation.id)}
            >
              Configurar Agendamento
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Analytics */}
      <Modal
        isOpen={showAnalyticsModal}
        onClose={() => setShowAnalyticsModal(false)}
        title="Analytics de Automação"
        size="lg"
      >
        {analytics && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold">{analytics.totalExecutions}</div>
                  <div className="text-sm text-gray-600 dark:text-slate-300">Total de Execuções</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold">{analytics.totalEmailsSent}</div>
                  <div className="text-sm text-gray-600 dark:text-slate-300">Emails Enviados</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold">{analytics.totalEmailsFailed}</div>
                  <div className="text-sm text-gray-600 dark:text-slate-300">Emails Falhados</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-2xl font-bold">{Math.round(analytics.averageExecutionTime)}ms</div>
                  <div className="text-sm text-gray-600 dark:text-slate-300">Tempo Médio</div>
                </CardContent>
              </Card>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-2">Tendências</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className={`text-lg font-bold ${analytics.trends.emailsSentTrend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {analytics.trends.emailsSentTrend > 0 ? '+' : ''}{analytics.trends.emailsSentTrend.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-slate-300">Emails Enviados</div>
                </div>
                <div className="text-center">
                  <div className={`text-lg font-bold ${analytics.trends.successRateTrend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {analytics.trends.successRateTrend > 0 ? '+' : ''}{analytics.trends.successRateTrend.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-slate-300">Taxa de Sucesso</div>
                </div>
                <div className="text-center">
                  <div className={`text-lg font-bold ${analytics.trends.executionTimeTrend > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {analytics.trends.executionTimeTrend > 0 ? '+' : ''}{analytics.trends.executionTimeTrend.toFixed(1)}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-slate-300">Tempo de Execução</div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-2">Métricas Diárias</h3>
              <div className="max-h-64 overflow-y-auto">
                {Array.isArray(analytics.dailyMetrics) && analytics.dailyMetrics.map((metric, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b">
                    <span className="font-medium">{metric.date}</span>
                    <div className="flex gap-4 text-sm">
                      <span>{metric.executions} execuções</span>
                      <span>{metric.emailsSent} emails</span>
                      <span>{metric.emailsFailed} falhas</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EmailAutomationPage;
